import React, { useCallback, useEffect, useState, memo } from "react";
import { Row } from "react-bootstrap";
import "../RegisteredUser/WalletPage.css";
import toast, { Toaster } from "react-hot-toast";
import instance, { apiEncryptRequest, decryptData } from "../../Api";
import { useAuth } from "../../AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import Loader from "../common/Loader";
import AppToast from "../../AppToast";

// ─── Pure helpers ─────────────────────────────────────────────────────────────
const isMobileDevice = () => window.matchMedia("(max-width: 800px)").matches;

const diskGB = (item) =>
  item.disk_type === "nvme" ? item.nvme : item.disk_type === "hdd" ? item.hard_disk : item.ssd;

// ─── Assign button — identical in all 4 card copies ──────────────────────────
const AssignBtn = memo(({ onClick, minWidth = "9rem" }) => (
  <div className="log-in" style={{ marginBottom: 6 }} onClick={onClick}>
    <a className="media-link">
      <div className="media-banner" style={{ minWidth, marginTop: 10 }}>
        <img className="normal-banner"    src="/admin/images/admin/wallet/add-money-btn.png" alt="" />
        <img className="hover-img-banner" src="/admin/images/admin/wallet/recharge-btn.png"  alt="" />
        <span className="login-text" style={{ color: "white", fontSize: 20, marginTop: 0, fontWeight: 600 }}
          onMouseOver={(e) => (e.currentTarget.style.color = "#07528B")}
          onMouseOut={(e)  => (e.currentTarget.style.color = "white")}>
          Assign
        </span>
      </div>
    </a>
  </div>
));

// ─── VM card — replaces mobile + web copies ───────────────────────────────────
const VMCard = memo(({ item, isMobile, onAssign }) => (
  <div className="wallet-col" style={isMobile ? undefined : { justifyContent: "normal", gap: 50 }}>
    {/* Name badge */}
    <div style={{ padding: "0rem 1rem" }}>
      <img src="/admin/images/admin/wallet/name-bg.png" alt="" />
      <div style={{ marginTop: isMobile ? "-50px" : "-4.5rem", position: "relative", zIndex: 9, height: isMobile ? 60 : "4rem", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={isMobile ? { textAlign: "center", fontSize: 18, color: "black", fontWeight: 500 } : undefined}>
          {item.vm_name}
        </p>
      </div>
    </div>

    {/* Specs */}
    <div style={{ padding: "0rem 1rem" }}>
      <img src={isMobile ? "/admin/images/admin_old/new-mobile-icons/wallet/machine-config-bg.png" : "/admin/images/admin/wallet/details-bg.png"}
        alt="" style={isMobile ? { height: "12rem" } : undefined} />
      <div style={{ marginTop: isMobile ? "-11.5rem" : "-4.5rem", position: "relative", zIndex: 9, height: isMobile ? "4rem" : "4rem", display: "flex", alignItems: "center", flexDirection: isMobile ? "column" : undefined, justifyContent: isMobile ? undefined : "space-evenly" }}>
        <div className="details-label">{item.cpu}<span> CPU</span></div>
        <div className="details-label">{item.ram / 1024} GB<span> RAM</span></div>
        <div className="details-label">{diskGB(item)} GB<span> SSD Disk</span></div>
        <div className="details-label">{item.data_transfer} TB<span> Bandwidth</span></div>
      </div>
    </div>

    {/* Assign */}
    <div style={isMobile ? { marginTop: 130, padding: "0rem 1rem" } : undefined}>
      <AssignBtn onClick={() => onAssign(item.vm_id)} minWidth={isMobile ? "9rem" : "12rem"} />
    </div>
  </div>
));

// ─── CDN card — replaces mobile + web copies ─────────────────────────────────
const CDNCard = memo(({ item, isMobile, onAssign }) => (
  <div className="wallet-col" style={isMobile ? undefined : { marginLeft: 50, justifyContent: "normal", gap: 50 }}>
    {/* URL badge */}
    <div style={{ padding: "0rem 1rem" }}>
      <img src="/admin/images/admin/wallet/name-bg.png" alt="" />
      <div style={{ fontSize: isMobile ? 14 : 18, marginTop: isMobile ? "-4.5rem" : "-4.5rem", position: "relative", zIndex: 9, height: "4rem", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {item.website_url}
      </div>
    </div>

    {/* Data stats */}
    <div style={{ width: isMobile ? "100%" : undefined, padding: "0rem 1rem", marginTop: isMobile ? 90 : undefined }}>
      {!isMobile && <img src="/admin/images/admin/wallet/details-bg.png" alt="" />}
      <div style={{ marginTop: isMobile ? "-4.5rem" : "-4.5rem", position: "relative", zIndex: 9, height: "4rem", display: "flex", alignItems: "center", justifyContent: isMobile ? "space-evenly" : "space-evenly", flexDirection: isMobile ? "column" : undefined, textAlign: isMobile ? "center" : undefined }}>
        <div className="details-label" style={{ fontSize: isMobile ? 16 : 18 }}>Total Data: {item.datatransfer_value} TB</div>
        <div className="details-label" style={{ fontSize: isMobile ? 16 : 18 }}>Remaining Data: {item.remaining_data} TB</div>
      </div>
    </div>

    {/* Assign */}
    <div style={isMobile ? { marginTop: 20 } : undefined}>
      <AssignBtn onClick={() => onAssign(item.cdn_id)} />
    </div>
  </div>
));

// ─── Main Component ───────────────────────────────────────────────────────────
const VMAssignPage = () => {
  const location      = useLocation();
  const navigate      = useNavigate();
  const { smuser }    = useAuth();

  const selectedUser  = location.state?.userDetail  ?? null;
  const isServerOrCDN = location.state?.isServerOrCDN ?? null;

  const [isMobile,    setIsMobile]    = useState(isMobileDevice);
  const [walletData,  setWalletData]  = useState([]);
  const [cdnData,     setCDNData]     = useState([]);
  const [loading,     setLoading]     = useState(true);

  // ── Toast helper ─────────────────────────────────────────────────────────
  const showToast = useCallback((msg) =>
    toast((t) => <AppToast id={t.id} message={msg} isMobile={isMobile} />),
    [isMobile]);

  // ── API helper ────────────────────────────────────────────────────────────
  const apiPost = useCallback(async (url, payload) => {
    const enc = await apiEncryptRequest(payload);
    const res = await instance.post(url, enc);
    return decryptData(res.data);
  }, []);

  // ── Fetch VM + CDN list ───────────────────────────────────────────────────
  const getVMList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiPost("/wallet", { user_id: smuser.id });
      setWalletData(Object.values(data.vm));
      setCDNData(Object.values(data.cdn));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [smuser?.id, apiPost]);

  // ── Mount — fix: original had [isMobile] as dep, causing getVMList() to
  // re-fire on every window resize. Changed to [].
  useEffect(() => {
    getVMList();
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── Assign VM ─────────────────────────────────────────────────────────────
  const assignVM = useCallback(async (vmid) => {
    setLoading(true);
    try {
      const data = await apiPost("/assignvm", { user_id: selectedUser.id, parent_id: smuser.id, vm_id: vmid });
      showToast(data.message);
      if (data.status) navigate("/assigned-vm", { state: { userDetail: selectedUser } });
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedUser, smuser?.id, apiPost, navigate, showToast]);

  // ── Assign CDN ────────────────────────────────────────────────────────────
  const assignCDN = useCallback(async (cdnid) => {
    setLoading(true);
    try {
      const data = await apiPost("/assigncdn", { user_id: selectedUser.id, parent_id: smuser.id, cdn_id: cdnid });
      showToast(data.message);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedUser, smuser?.id, apiPost, showToast]);

  const isServer = isServerOrCDN === "SERVER";
  const heading  = isServer ? "VM LIST" : "CDN LIST";

  // ── Shared card list — used in both mobile and web ────────────────────────
  const cardList = (
    isServer ? (
      <div className={isMobile ? "wallet-box" : "wallet-box-main"}>
        {walletData.map((item, idx) => (
          <VMCard key={item.vm_id ?? idx} item={item} isMobile={isMobile} onAssign={assignVM} />
        ))}
      </div>
    ) : (
      <div className={isMobile ? "wallet-box" : "wallet-box-main"}>
        {cdnData.map((item, idx) => (
          <CDNCard key={item.cdn_id ?? idx} item={item} isMobile={isMobile} onAssign={assignCDN} />
        ))}
      </div>
    )
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ width: "100%", minHeight: "65rem", position: "relative", backgroundImage: "url(/main-bg.jpg)", backgroundSize: "cover", backgroundRepeat: "round", backgroundBlendMode: "overlay" }}>
      <div className="apptoast-align">
        <Toaster position={isMobile ? "top-center" : "bottom-right"} reverseOrder={false} />
      </div>

      {isMobile ? (
        /* ── MOBILE ──────────────────────────────────────────────────────── */
        <div style={{ height: "50rem" }}>
          <div className="heading-dotted-support">{heading} <span /></div>
          <div className="features-section-solution" style={{ marginTop: 0 }}>
            <div style={{ position: "relative", marginTop: "1rem" }}>
              <div className="wallet-container" style={{ width: "90%" }}>
                {cardList}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* ── WEB ─────────────────────────────────────────────────────────── */
        <div className="features-page-solution" style={{ height: "75rem", padding: "5rem" }}>
          <div className="heading-dotted-support">{heading} <span /></div>
          <div className="features-section-solution">
            <Row>
              <div className="col-md-1" />
              <div className={isServer ? "col-md-11" : "col-md-10"}>
                {isServer ? (
                  <div className="features-section-solution">
                    <Row>
                      <div className="col-md-1" />
                      <div className="col-md-10">
                        <div style={{ position: "relative", marginTop: "1rem" }}>
                          <div className="wallet-container">{cardList}</div>
                        </div>
                      </div>
                    </Row>
                  </div>
                ) : (
                  <div className="wallet-container" style={{ marginTop: 30, marginLeft: 100 }}>
                    {cardList}
                  </div>
                )}
              </div>
            </Row>
          </div>
        </div>
      )}

      {loading && (
        <div className="loading-overlay" style={{ zIndex: "9999999999999999" }}>
          <Loader isLoading={loading} />
        </div>
      )}
    </div>
  );
};

export default VMAssignPage;